import React, {useEffect} from 'react'
import type {IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import type {Block} from '@/services/kontent-ai/types'

export type PearCommerceWidgetContentItem = IContentItem<{
	// No specific elements needed for this component
}>

export const PearCommerceWidget: Block<PearCommerceWidgetContentItem> = () => {
	const router = useRouter()

	useEffect(() => {
		const {sku, offerId} = router.query

		// Ensure sku and offerId are strings
		const skuString = Array.isArray(sku) ? sku[0] : sku
		const offerIdString = Array.isArray(offerId) ? offerId[0] : offerId

		// Only proceed if both sku and offerId are available
		if (!skuString || !offerIdString) {
			// eslint-disable-next-line no-console -- Log error details
			console.error(
				'Missing required parameters - sku or offerId not found'
			)
			return
		}

		// Flag to track if we've already updated the iframe
		let iframeUpdated = false

		// Function to update the iframe with sku and offerId
		const updateIframe = (): void => {
			if (iframeUpdated) return

			const pearFrame = document.querySelector('#pear-pdp-target iframe')
			if (pearFrame && pearFrame instanceof HTMLIFrameElement) {
				try {
					const currentUrl = new URL(pearFrame.src)

					const pathParts = currentUrl.pathname.split('/')
					const labelIndex = pathParts.indexOf('agnostic-pdp-label')
					if (labelIndex !== -1 && pathParts[labelIndex + 1]) {
						pathParts[labelIndex + 1] = offerIdString
						currentUrl.pathname = pathParts.join('/')
					}

					const searchParams = new URLSearchParams(currentUrl.search)
					searchParams.set('sku', skuString)
					searchParams.set('offerId', offerIdString)
					searchParams.set(
						'childId',
						`pear-widget-iframe-wrap-${offerIdString}`
					)

					const parentUrl = searchParams.get('parentUrl')
					if (parentUrl) {
						try {
							const decodedParentUrl = new URL(
								decodeURIComponent(parentUrl)
							)

							const parentSearchParams = new URLSearchParams(
								decodedParentUrl.search
							)
							parentSearchParams.set('sku', skuString)
							parentSearchParams.set('offerId', offerIdString)

							decodedParentUrl.search =
								parentSearchParams.toString()

							searchParams.set(
								'parentUrl',
								decodedParentUrl.toString()
							)
						} catch (parentUrlError) {
							// eslint-disable-next-line no-console -- log error details
							console.error(
								'Error updating parentUrl:',
								parentUrlError
							)
						}
					}

					// Update the URL with new search parameters
					currentUrl.search = searchParams.toString()

					const newSrc = currentUrl.toString()
					pearFrame.src = newSrc
					iframeUpdated = true
				} catch (error) {
					// eslint-disable-next-line no-console -- Log error details
					console.error('Error updating iframe URL:', error)
				}
			}
		}

		// Try updating immediately in case iframe is already present
		updateIframe()

		// If iframe wasn't found, observe for it to be added by Pear SDK
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- iframeUpdated is mutated by updateIframe
		if (!iframeUpdated) {
			const targetNode = document.getElementById('pear-pdp-target')
			if (!targetNode) {
				return
			}

			const observer = new MutationObserver(() => {
				updateIframe()
				if (iframeUpdated) {
					observer.disconnect()
				}
			})

			observer.observe(targetNode, {
				childList: true,
				subtree: true,
			})

			// Clean up observer on unmount
			return () => {
				observer.disconnect()
			}
		}
	}, [router.query])

	return (
		<div
			data-kontent-element-codename="pear_commerce_widget_guidelines"
			id="pear-pdp-target"
		>
			{/* Pear Commerce will inject its content here */}
		</div>
	)
}
